import { Modal } from 'antd'
import connect from '../../../../../helpers/api/connect'
import Calculation from '../../../../../types/api/calculation'
import Shipment from '../../../../../types/api/shipment'
import { StrapiCreate } from '../../../../../types/strapi/strapi'
import { ListItem, MaterialRequirements as MaterialRequirementsValue } from '../types/form-values'
import { buildShipmentStampFieldsFromCalcPos } from '../helpers/build-shipment-stamp-fields-from-calc-pos'

type CreateShipment = StrapiCreate<Shipment>
type CreateShipmentMaterialRequirement = Exclude<
    CreateShipment['data']['materialRequirements'],
    undefined
>[number]
type CreateShipmentMaterialRequirementsConfiguration = Exclude<
    CreateShipmentMaterialRequirement['configurations'],
    undefined
>[number]

export default function useCreateShipmentQueryArgExtractor(calculation: Calculation) {
    const getConfigurations = (requirements: ListItem['materialRequirements']) => {
        const list = requirements.configurations
        const payloadList: CreateShipmentMaterialRequirementsConfiguration[] = []

        for (const config of list) {
            if (!config.material || typeof config.material !== 'string') {
                Modal.error({ title: 'Выберите ширину из списка' })
                return []
            }

            payloadList.push({
                amount: config.amount,
                action: config.action,
                material: { connect: [config.material] },
            })
        }

        return payloadList
    }

    const extractMaterialRequirement = (mr: MaterialRequirementsValue) => {
        return {
            material_type: { connect: [mr.materialTypeDocId] },
            configurations: getConfigurations(mr),
        }
    }

    const extractMaterialRequirements = (item: ListItem) => {
        const requirements: CreateShipmentMaterialRequirement[] = []

        if (item.materialRequirements) {
            requirements.push(extractMaterialRequirement(item.materialRequirements))
        }

        if (item.additionalMaterialRequirements) {
            for (const materialRequirements of item.additionalMaterialRequirements) {
                requirements.push(extractMaterialRequirement(materialRequirements))
            }
        }

        return requirements
    }

    const getPreviousBlankPreviewFileIds = (item: ListItem) => {
        if (!item.reusePreviousBlankPreview) return []

        return (
            item.calculationPosition.shipment_position?.shipment?.blankPreviewFiles?.map(
                file => file.id
            ) ?? []
        )
    }

    return async (
        item: ListItem,
        shipmentPositionDocIds: string[],
        sourceFileIds: number[],
        orderDocumentId?: string
    ): Promise<StrapiCreate<Shipment>> => {
        const calcPos = item.calculationPosition
        const sourceFormSet = calcPos.shipment_position?.shipment?.form_set
        const previousBlankPreviewFileIds = getPreviousBlankPreviewFileIds(item)
        const stampFields = buildShipmentStampFieldsFromCalcPos(calcPos)

        return {
            data: {
                comment: item.comment,
                shipment_positions: { set: shipmentPositionDocIds },
                client: { set: [calculation.client!.documentId] },
                ...(orderDocumentId ? { order: { set: [orderDocumentId] } } : {}),
                ...stampFields,
                whitewash: calcPos.whitewash,
                cmyk: calcPos.cmyk,
                pantones: calcPos.pantones,
                sellingPrice: calcPos.sellingPrice,
                shipmentType: calcPos.shipmentType ?? 'label',
                quantityPerRoll: item.quantityPerRoll,
                windingInfo: item.windingInfo,
                designer: item.designer ? { connect: [item.designer] } : undefined,
                windingSchemeFace: item.windingSchemeFace,
                windingSchemeTurn: item.windingSchemeTurn,
                package: item.package,
                packageCopies: item.packageCopies,
                manualLabeling: item.manualLabeling,
                clientLabel: item.clientLabel,
                pallet: item.pallet,
                congreve: item.congreve,
                priming: item.priming,
                bushing: item.bushing,
                printingOnGlueLayer: item.printingOnGlueLayer,
                lacquers: item.lacquers.map(item => ({
                    type: { connect: [item.type] },
                    name: item.name,
                })),
                lacquersDms: calcPos.lacquersDms,
                dmsReserve: calcPos.dmsReserve,
                machine: calcPos.machine ? connect(calcPos.machine) : undefined,
                additional_machines: {
                    set: (item.calculationPosition.additional_machines || []).map(
                        x => x.documentId
                    ),
                },
                additional_jobs: {
                    set: (item.calculationPosition.additional_jobs || []).map(x => x.documentId),
                },
                materialRequirements: extractMaterialRequirements(item),
                clientDeliveryDate: item.clientDeliveryDate.toISOString(),
                paintCostOf1000DurstWG: calcPos.paintCostOf1000DurstWG,
                currencyRate: calcPos.currencyRate,
                numberOfForms: calcPos.numberOfForms,
                stampIncludedInCost: calcPos.stampIncludedInCost !== false,
                formsIncludedInCost: calcPos.formsIncludedInCost !== false,
                form_set: sourceFormSet?.documentId
                    ? { set: [sourceFormSet.documentId] }
                    : undefined,
                //@ts-ignore
                sourceFile: sourceFileIds,
                //@ts-ignore
                blankPreviewFiles: previousBlankPreviewFileIds.length
                    ? previousBlankPreviewFileIds
                    : undefined,
            },
        }
    }
}
